import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fmtDop } from "../helpers";

/**
 * AdminAffiliates — admin-only management of the affiliate / referral program.
 *
 *  - Top referrers (count of completed referrals + points balance).
 *  - Cashout configuration (point value DOP, min cashout, enable switch).
 *  - Manual payout creation (admin discretion only).
 *  - History of payouts with status transitions.
 */
export function AdminAffiliates({
  lang,
  toast,
}: {
  lang: string;
  $?: (n: number) => string;
  toast?: (m: string) => void;
}) {
  // All affiliate amounts (amount_dop, point value, cashout min) are DOP — never convert.
  const $ = fmtDop;
  const t = (es: string, en: string) => (lang === "es" ? es : en);

  const [settings, setSettings] = useState<any>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [topRefs, setTopRefs] = useState<any[]>([]);
  const [loadingTop, setLoadingTop] = useState(true);

  const [payouts, setPayouts] = useState<any[]>([]);
  const [loadingPayouts, setLoadingPayouts] = useState(true);

  // Manual payout form
  const [userEmail, setUserEmail] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [pointsUsed, setPointsUsed] = useState<number>(0);
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  const loadSettings = useCallback(async () => {
    const { data } = await supabase
      .from("payment_admin_settings")
      .select("id, affiliate_point_value_dop, affiliate_min_cashout_dop, affiliate_cashout_enabled")
      .limit(1)
      .maybeSingle();
    setSettings(data || {
      id: null,
      affiliate_point_value_dop: 1,
      affiliate_min_cashout_dop: 1000,
      affiliate_cashout_enabled: false,
    });
  }, []);

  const loadTop = useCallback(async () => {
    setLoadingTop(true);
    // top referrers by completed referrals
    const { data: refs } = await supabase
      .from("referrals")
      .select("referrer_id, status, reward_points")
      .eq("status", "completed")
      .limit(2000);
    const agg: Record<string, { count: number; points: number }> = {};
    (refs || []).forEach((r: any) => {
      const k = r.referrer_id;
      if (!agg[k]) agg[k] = { count: 0, points: 0 };
      agg[k].count += 1;
      agg[k].points += Number(r.reward_points || 0);
    });
    const ids = Object.keys(agg).sort((a, b) => agg[b].count - agg[a].count).slice(0, 25);
    if (ids.length === 0) { setTopRefs([]); setLoadingTop(false); return; }
    const [{ data: profs }, { data: balances }] = await Promise.all([
      supabase.from("profiles").select("user_id, first_name, last_name, email, referral_tier").in("user_id", ids),
      supabase.from("reward_points").select("user_id, balance").in("user_id", ids),
    ]);
    // latest balance per user
    const balMap: Record<string, number> = {};
    (balances || []).forEach((b: any) => {
      const cur = balMap[b.user_id] ?? -1;
      if (Number(b.balance ?? 0) > cur) balMap[b.user_id] = Number(b.balance ?? 0);
    });
    const rows = ids.map(id => {
      const p: any = (profs || []).find((x: any) => x.user_id === id) || {};
      return {
        user_id: id,
        name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || (p.email || id.slice(0, 8)),
        email: p.email || "",
        tier: p.referral_tier || "bronze",
        completed: agg[id].count,
        points_earned: agg[id].points,
        balance: balMap[id] ?? 0,
      };
    });
    setTopRefs(rows);
    setLoadingTop(false);
  }, []);

  const loadPayouts = useCallback(async () => {
    setLoadingPayouts(true);
    const { data } = await supabase
      .from("affiliate_payouts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(60);
    setPayouts(data || []);
    setLoadingPayouts(false);
  }, []);

  useEffect(() => { loadSettings(); loadTop(); loadPayouts(); }, [loadSettings, loadTop, loadPayouts]);

  const totalPending = useMemo(
    () => payouts.filter((p) => p.status === "pending").reduce((s, p) => s + Number(p.amount_dop || 0), 0),
    [payouts],
  );
  const totalPaid = useMemo(
    () => payouts.filter((p) => p.status === "paid").reduce((s, p) => s + Number(p.amount_dop || 0), 0),
    [payouts],
  );

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const payload = {
        affiliate_point_value_dop: Number(settings.affiliate_point_value_dop || 1),
        affiliate_min_cashout_dop: Number(settings.affiliate_min_cashout_dop || 1000),
        affiliate_cashout_enabled: !!settings.affiliate_cashout_enabled,
      };
      if (settings.id) {
        await supabase.from("payment_admin_settings").update(payload).eq("id", settings.id);
      } else {
        await supabase.from("payment_admin_settings").insert(payload);
      }
      toast?.(t("✅ Configuración guardada", "✅ Settings saved"));
      await loadSettings();
    } catch (e: any) {
      toast?.(`❌ ${e.message || e}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const fillFromUser = async (row: any) => {
    setUserEmail(row.email);
    const ptVal = Number(settings?.affiliate_point_value_dop || 1);
    setPointsUsed(row.balance);
    setAmount(Number((row.balance * ptVal).toFixed(2)));
  };

  const createPayout = async () => {
    if (!userEmail || amount <= 0) {
      toast?.(t("❌ Email y monto requeridos", "❌ Email and amount required"));
      return;
    }
    setCreating(true);
    try {
      const { data: prof, error: pErr } = await supabase
        .from("profiles").select("user_id").ilike("email", userEmail).maybeSingle();
      if (pErr) throw pErr;
      if (!prof?.user_id) throw new Error(t("Usuario no encontrado", "User not found"));
      const { data: me } = await supabase.auth.getUser();
      const { error } = await supabase.from("affiliate_payouts").insert({
        user_id: prof.user_id,
        amount_dop: amount,
        points_used: pointsUsed,
        method, reference, notes,
        status: "pending",
        created_by: me?.user?.id ?? null,
      });
      if (error) throw error;
      toast?.(t("✅ Pago creado en estado pendiente", "✅ Payout created (pending)"));
      setUserEmail(""); setAmount(0); setPointsUsed(0); setReference(""); setNotes("");
      await loadPayouts();
    } catch (e: any) {
      toast?.(`❌ ${e.message || e}`);
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (id: string, status: "paid" | "cancelled") => {
    const patch: any = { status };
    if (status === "paid") patch.paid_at = new Date().toISOString();
    const { error } = await supabase.from("affiliate_payouts").update(patch).eq("id", id);
    if (error) { toast?.(`❌ ${error.message}`); return; }
    toast?.(t("✅ Estado actualizado", "✅ Status updated"));
    await loadPayouts();
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="fcard">
        <h4>{t("Configuración del programa", "Program settings")}</h4>
        {!settings ? <p style={{ color: "var(--tx3)" }}>…</p> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
            <label style={{ fontSize: 11 }}>
              {t("Valor del punto (RD$)", "Point value (DOP)")}
              <input type="number" step="0.01" min="0" value={settings.affiliate_point_value_dop}
                onChange={(e) => setSettings({ ...settings, affiliate_point_value_dop: e.target.value })}
                style={{ width: "100%", padding: 6, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 11 }}>
              {t("Mínimo cashout (RD$)", "Min cashout (DOP)")}
              <input type="number" step="1" min="0" value={settings.affiliate_min_cashout_dop}
                onChange={(e) => setSettings({ ...settings, affiliate_min_cashout_dop: e.target.value })}
                style={{ width: "100%", padding: 6, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 11, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={!!settings.affiliate_cashout_enabled}
                  onChange={(e) => setSettings({ ...settings, affiliate_cashout_enabled: e.target.checked })} />
                {t("Habilitar cashout (interno)", "Enable cashout (internal)")}
              </span>
              <span style={{ fontSize: 10, color: "var(--tx3)", marginTop: 4 }}>
                {t("Aunque esté activo, los usuarios no podrán solicitarlo desde su UI: el pago lo emite el admin.",
                  "Even when on, users can't request cashout from their UI: only the admin issues payouts.")}
              </span>
            </label>
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <button className="btn bp" disabled={savingSettings} onClick={saveSettings}>
            {savingSettings ? "…" : t("Guardar", "Save")}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
        <div className="fcard" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>{t("Pendiente", "Pending")}</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{$(totalPending)}</div>
        </div>
        <div className="fcard" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>{t("Pagado", "Paid")}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--ac)" }}>{$(totalPaid)}</div>
        </div>
        <div className="fcard" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>{t("Top referidores activos", "Top active referrers")}</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{topRefs.length}</div>
        </div>
      </div>

      <div className="fcard">
        <h4>{t("Top referidores", "Top referrers")}</h4>
        {loadingTop ? <p style={{ color: "var(--tx3)" }}>…</p> : topRefs.length === 0 ? (
          <p style={{ color: "var(--tx3)", fontSize: 12 }}>
            {t("Aún no hay referidos completados.", "No completed referrals yet.")}
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg3)" }}>
                  <th style={th}>{t("Usuario", "User")}</th>
                  <th style={th}>Tier</th>
                  <th style={th}>{t("Completados", "Completed")}</th>
                  <th style={th}>{t("Pts ganados", "Pts earned")}</th>
                  <th style={th}>{t("Pts disponibles", "Pts available")}</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {topRefs.map(r => (
                  <tr key={r.user_id} style={{ borderBottom: "1px solid var(--bd2)" }}>
                    <td style={td}><b>{r.name}</b><div style={{ fontSize: 10, color: "var(--tx3)" }}>{r.email}</div></td>
                    <td style={td}>{r.tier}</td>
                    <td style={td}>{r.completed}</td>
                    <td style={td}>{r.points_earned}</td>
                    <td style={td}>{r.balance}</td>
                    <td style={td}>
                      <button className="btn bo" style={{ fontSize: 10, padding: "4px 8px" }} onClick={() => fillFromUser(r)}>
                        {t("Emitir pago", "Issue payout")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="fcard">
        <h4>{t("Emitir pago manual", "Issue manual payout")}</h4>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 8 }}>
          <label style={{ fontSize: 11 }}>
            {t("Email del referidor", "Referrer email")}
            <input value={userEmail} onChange={(e) => setUserEmail(e.target.value)}
              placeholder="user@example.com" style={{ width: "100%", padding: 6, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 11 }}>
            {t("Monto (RD$)", "Amount (DOP)")}
            <input type="number" min="0" step="0.01" value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              style={{ width: "100%", padding: 6, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 11 }}>
            {t("Puntos descontados", "Points consumed")}
            <input type="number" min="0" step="1" value={pointsUsed}
              onChange={(e) => setPointsUsed(Number(e.target.value))}
              style={{ width: "100%", padding: 6, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 11 }}>
            {t("Método", "Method")}
            <select value={method} onChange={(e) => setMethod(e.target.value)}
              style={{ width: "100%", padding: 6, marginTop: 4 }}>
              <option value="bank_transfer">{t("Transferencia bancaria", "Bank transfer")}</option>
              <option value="store_credit">{t("Crédito en tienda", "Store credit")}</option>
              <option value="cash">{t("Efectivo", "Cash")}</option>
              <option value="other">{t("Otro", "Other")}</option>
            </select>
          </label>
          <label style={{ fontSize: 11 }}>
            {t("Referencia", "Reference")}
            <input value={reference} onChange={(e) => setReference(e.target.value)}
              style={{ width: "100%", padding: 6, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 11, gridColumn: "1 / -1" }}>
            {t("Notas internas", "Internal notes")}
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              style={{ width: "100%", padding: 6, marginTop: 4 }} />
          </label>
        </div>
        <button className="btn bp" disabled={creating} onClick={createPayout} style={{ marginTop: 10 }}>
          {creating ? "…" : t("Crear pago pendiente", "Create pending payout")}
        </button>
      </div>

      <div className="fcard">
        <h4>{t("Historial de pagos", "Payout history")}</h4>
        {loadingPayouts ? <p style={{ color: "var(--tx3)" }}>…</p> : payouts.length === 0 ? (
          <p style={{ color: "var(--tx3)", fontSize: 12 }}>{t("Sin pagos registrados.", "No payouts on record.")}</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg3)" }}>
                  <th style={th}>{t("Fecha", "Date")}</th>
                  <th style={th}>{t("Usuario", "User")}</th>
                  <th style={th}>{t("Monto", "Amount")}</th>
                  <th style={th}>{t("Pts", "Pts")}</th>
                  <th style={th}>{t("Método", "Method")}</th>
                  <th style={th}>{t("Estado", "Status")}</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id} style={{ borderBottom: "1px solid var(--bd2)" }}>
                    <td style={td}>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td style={td}>{p.user_id.slice(0, 8)}</td>
                    <td style={td}>{$(Number(p.amount_dop))}</td>
                    <td style={td}>{p.points_used}</td>
                    <td style={td}>{p.method || "—"}</td>
                    <td style={td}>
                      <span style={{
                        padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                        background: p.status === "paid" ? "#ECFDF5" : p.status === "pending" ? "#FEF3C7" : "#FEE2E2",
                        color: p.status === "paid" ? "#065F46" : p.status === "pending" ? "#92400E" : "#991B1B",
                      }}>{p.status}</span>
                    </td>
                    <td style={td}>
                      {p.status === "pending" && (
                        <>
                          <button className="btn bp" style={{ fontSize: 10, padding: "3px 6px", marginRight: 4 }}
                            onClick={() => updateStatus(p.id, "paid")}>
                            {t("Marcar pagado", "Mark paid")}
                          </button>
                          <button className="btn bo" style={{ fontSize: 10, padding: "3px 6px" }}
                            onClick={() => updateStatus(p.id, "cancelled")}>
                            {t("Cancelar", "Cancel")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: "left", padding: "8px 6px", fontWeight: 700, fontSize: 11, color: "var(--tx2)" };
const td: React.CSSProperties = { padding: "8px 6px", verticalAlign: "top" };